export type ToastVariant = "error" | "info"

export type ShowToastOptions = {
  durationMs?: number
  variant?: ToastVariant
}

const TOAST_ROOT_ID = "app-toast-root"
const DEFAULT_DURATION_MS = 5000

let toastRoot: HTMLDivElement | null = null

function ensureToastRoot(): HTMLDivElement | null {
  if (typeof document === "undefined") {
    return null
  }

  if (toastRoot && document.body.contains(toastRoot)) {
    return toastRoot
  }

  const existing = document.getElementById(TOAST_ROOT_ID)
  if (existing instanceof HTMLDivElement) {
    toastRoot = existing
    return toastRoot
  }

  const root = document.createElement("div")
  root.id = TOAST_ROOT_ID
  root.setAttribute("role", "status")
  root.setAttribute("aria-live", "polite")
  root.className = "fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
  document.body.appendChild(root)
  toastRoot = root
  return toastRoot
}

function removeToastRootIfEmpty(root: HTMLDivElement): void {
  if (root.childElementCount > 0) {
    return
  }
  root.remove()
  if (toastRoot === root) {
    toastRoot = null
  }
}

/**
 * ライブラリ非依存の軽量トースト。tailwind ユーティリティクラスで表示する。
 */
export function showToast(messageText: string, options: ShowToastOptions = {}): void {
  const root = ensureToastRoot()
  if (!root) {
    return
  }

  const variant = options.variant ?? "error"
  const durationMs = options.durationMs ?? DEFAULT_DURATION_MS

  const el = document.createElement("div")
  el.className =
    variant === "error"
      ? "pointer-events-auto max-w-sm rounded-md bg-red-600 px-4 py-3 text-sm text-white shadow-lg"
      : "pointer-events-auto max-w-sm rounded-md bg-gray-800 px-4 py-3 text-sm text-white shadow-lg"
  // エラーは assertive に読み上げ、info 等は root の polite status に任せる
  if (variant === "error") {
    el.setAttribute("role", "alert")
  } else {
    el.setAttribute("role", "status")
  }
  el.textContent = messageText
  root.appendChild(el)

  window.setTimeout(() => {
    el.remove()
    removeToastRootIfEmpty(root)
  }, durationMs)
}

/** テスト用: 表示中トーストとルートをクリアする */
export function clearToasts(): void {
  if (typeof document === "undefined") {
    return
  }
  const root = document.getElementById(TOAST_ROOT_ID)
  if (root) {
    root.remove()
  }
  toastRoot = null
}
